import React from "react";
import { Box, Chip, Typography, Button } from "@mui/material";
import { LeadThread, TeamUser } from "./leadApi";

interface Props {
  lead: LeadThread;
  /** Self-assign handler — when present and the lead is unassigned,
   *  the cell renders a compact "Assign to Me" button under the
   *  Unassigned chip. Click triggers parent which calls the claim
   *  API and refreshes the list. */
  onClaim?: (leadId: number) => void;
  /** Disabled while a claim is in flight (per-row). */
  claiming?: boolean;
}

/** Resolve a team user's best display name. */
function resolveName(u: TeamUser | null | undefined): string | null {
  if (!u) return null;
  return (
    u.display_name ||
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    null
  );
}

/** "May 14, 2026 • 3:42 PM" */
function fmtCreated(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " • " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
}

export const AssignedCell = React.memo(function AssignedCell({ lead, onClaim, claiming }: Props) {
  const assignee = resolveName(lead.assignee);
  const creator = resolveName(lead.creator);
  const created = fmtCreated(lead.created_at);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
      {/* Assigned To — name as plain executive text, or a quiet
          neutral "Unassigned" pill (was bright red, too alarming).
          When `onClaim` is provided and the lead is unassigned, a
          compact "Assign to Me" button is rendered immediately
          below so a Lead Manager can claim it in one click. */}
      {assignee ? (
        <Typography sx={{ fontSize: "0.78rem", color: "#1e293b", fontWeight: 600, lineHeight: 1.3 }}>
          {assignee}
        </Typography>
      ) : (
        <>
          <Chip
            label="Unassigned"
            size="small"
            sx={{
              bgcolor: "#fcfcfd", color: "#64748b",
              fontWeight: 600, fontSize: "0.66rem", height: 21,
              border: "1px solid #e5e7eb", borderRadius: "6px",
              alignSelf: "flex-start",
              "& .MuiChip-label": { px: 0.9 },
            }}
          />
          {onClaim && (
            <Button
              size="small"
              disableElevation
              disabled={!!claiming}
              onClick={(e) => { e.stopPropagation(); onClaim(lead.id); }}
              sx={{
                mt: 0.4, alignSelf: "flex-start",
                textTransform: "none", fontWeight: 700, fontSize: "0.64rem",
                py: 0.1, px: 0.9, minWidth: 0, height: 22,
                color: "#16233A", bgcolor: "#fff",
                border: "1px solid #16233A",
                borderRadius: "6px",
                "&:hover": { bgcolor: "#16233A", color: "#fff" },
                "&.Mui-disabled": { color: "#94a3b8", borderColor: "#e5e7eb" },
              }}
            >
              {claiming ? "Claiming…" : "Assign to Me"}
            </Button>
          )}
        </>
      )}

      {/* Created by / Created time */}
      {(creator || created) && (
        <Box>
          {creator && (
            <Typography sx={{ fontSize: "0.66rem", color: "#94a3b8", lineHeight: 1.35 }}>
              By {creator}
            </Typography>
          )}
          {created && (
            <Typography sx={{ fontSize: "0.64rem", color: "#cbd5e1", lineHeight: 1.35, fontVariantNumeric: "tabular-nums" }}>
              {created}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});
